import React from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Info} from '../../../icons';
import {formatFullMoney} from '../../../client/panel_v11/panelFormatters';
import {
    SOURCE_LABEL_KEYS,
    economyReasonText,
    formatEconomyShortDate,
    formatPct,
    formatRoas,
    formatSignedMoney,
} from '../../../client/panel_v11/economyFormatters';

// Las columnas de la vista Economía, en orden (copy §6). Las usan el encabezado y la fila: si una
// cambia de lugar, cambia en los dos. Qué columnas se ocultan en cada ancho lo deciden los tramos
// `cv2-eco-tier-*` de `_clients-v2.scss`, por la clase `cv2-eco-col-<key>`.
export const ECONOMICS_COLUMNS = [
    {key: 'state', labelKey: 'clients_v2.economics.col.state'},
    {key: 'margin', labelKey: 'clients_v2.economics.col.margin'},
    {key: 'shipping', labelKey: 'clients_v2.economics.col.shipping'},
    {key: 'fee', labelKey: 'clients_v2.economics.col.fee'},
    {key: 'fixed', labelKey: 'clients_v2.economics.col.fixed'},
    {key: 'agency_fee', labelKey: 'clients_v2.economics.col.agency_fee'},
    {key: 'breakeven', labelKey: 'clients_v2.economics.col.breakeven'},
    {key: 'profit', labelKey: 'clients_v2.economics.col.profit'},
    {key: 'effective', labelKey: 'clients_v2.economics.col.effective'},
];

// Las vistas del Listado 2.1. Viven acá (un módulo liviano) y no en la toolbar para que la fila no
// tenga que importar la toolbar, que arrastra el alta de cliente.
export const VIEW_SUMMARY = 'summary';
export const VIEW_ECONOMICS = 'economics';

export const STATE_OK = 'OK';
export const STATE_REVIEW = 'REVIEW';
export const STATE_NOT_CONFIGURED = 'NOT_CONFIGURED';
// El backend no pudo armar el bloque de ESE cliente (el resto del listado sale igual): no es «sin
// cargar» —la economía puede estar— y no se invita a cargarla.
export const STATE_UNAVAILABLE = 'UNAVAILABLE';

// La vista elegida se recuerda POR USUARIO (R-ecofe-20): el CTA «Cargar / Revisar» lleva a la
// pantalla del cliente y, al volver, el usuario caía en Resumen — y el «cargar y revisar todo en una
// sentada» del mockup 6 se cortaba en cada cliente. Es una comodidad: si el storage falla, Resumen.
const VIEW_STORAGE_PREFIX = 'dashbo_clients_v2_view_';

export function loadClientsView(userId) {
    try {
        const stored = window.localStorage.getItem(`${VIEW_STORAGE_PREFIX}${userId || 'anon'}`);
        return stored === VIEW_ECONOMICS ? VIEW_ECONOMICS : VIEW_SUMMARY;
    } catch (error) {
        return VIEW_SUMMARY;
    }
}

export function saveClientsView(userId, view) {
    try {
        window.localStorage.setItem(`${VIEW_STORAGE_PREFIX}${userId || 'anon'}`, view);
    } catch (error) {
        // Sin storage la vista no se recuerda; la pantalla anda igual.
    }
}

// «Atención primero» en la vista Economía: lo que pide mirar (Revisar), lo que falta cargar, lo que
// está al día. La severidad OPERATIVA del mes no ordena acá: es un criterio que esta vista no muestra
// (R-ecofe-21). Un estado desconocido va al final.
const ECONOMICS_ATTENTION_RANK = {REVIEW: 0, NOT_CONFIGURED: 1, OK: 2, ESCALAFY: 2, UNAVAILABLE: 3};

export function economicsAttentionRank(client) {
    const key = economicsStateKey(client && client.economics);
    return key in ECONOMICS_ATTENTION_RANK ? ECONOMICS_ATTENTION_RANK[key] : 3;
}

/**
 * Qué dice la pastilla de estado. `ESCALAFY` no es un estado del backend: es un perfil AL DÍA cuya
 * versión vigente vino sola de Escalafy (el mockup lo pinta aparte porque la agencia no tiene que
 * hacer nada). Revisar le gana: un perfil de Escalafy envejecido también pide mirarlo.
 */
export function economicsStateKey(economics) {
    const state = economics && economics.state;
    if (state === STATE_REVIEW) return STATE_REVIEW;
    if (state === STATE_OK) return economics.source === 'ESCALAFY' ? 'ESCALAFY' : STATE_OK;
    if (state === STATE_NOT_CONFIGURED) return STATE_NOT_CONFIGURED;
    if (state === STATE_UNAVAILABLE) return STATE_UNAVAILABLE;
    return null;
}

/**
 * Las celdas de la vista «Economía» de una fila del Listado 2.1 (bloque `economics` de
 * `agency/clients/overview/`, contrato §3.6). Los números son LOS MISMOS campos que la card de la
 * ficha y el modal: acá no se calcula nada.
 *
 * Sin cargar, la fila no inventa ceros: cada dato es «—» y la última celda lleva a cargarla.
 */
export function ClientEconomicsCells({client}) {
    const {t, i18n} = useTranslation();
    const economics = client.economics || {};
    const stateKey = economicsStateKey(economics);
    const configured = stateKey !== null && stateKey !== STATE_NOT_CONFIGURED && stateKey !== STATE_UNAVAILABLE;
    const currency = economics.currency || null;
    const economiaPath = `/client/${client.id}/economia`;
    const profit = Number(economics.estimated_profit);
    const dash = <span className="cv2-dash">—</span>;
    const stopRow = (event) => event.stopPropagation();

    const value = (text) => (configured && text ? text : dash);
    // Un «—» con motivo (monedas distintas, sin tienda, margen que no alcanza) lo dice en un ⓘ: un
    // cliente cargado con monedas mezcladas no puede verse igual que uno sin datos (R-ecofe-22).
    const reasons = economics.unavailable_reason || {};
    const valueOrWhy = (text, field) => {
        if (!configured || text) return value(text);
        const why = economyReasonText(reasons[field], t, {totals: true});
        if (!why) return dash;
        return (
            <span className="cv2-eco-why" data-testid={`cv2-eco-why-${field}-${client.id}`}>
                {dash}
                <span className="info-icon-container" onClick={stopRow} role="img" aria-label={why}>
                    <Info size={13} weight="bold"/>
                    <span className="warning-tooltip -below-right">
                        <span className="warning-tooltip-body">
                            <span className="warning-tooltip-text">{why}</span>
                        </span>
                    </span>
                </span>
            </span>
        );
    };

    const renderState = () => {
        if (!stateKey) return dash;
        return (
            <span className={`cv2-eco-pill -${stateKey.toLowerCase()}`}>
                {t(`clients_v2.economics.state.${stateKey}`)}
            </span>
        );
    };

    const renderEffective = () => {
        if (!configured || !economics.effective_from) return dash;
        const sourceKey = SOURCE_LABEL_KEYS[economics.source];
        return (
            <span className="cv2-eco-effective">
                <span>{formatEconomyShortDate(economics.effective_from, i18n && i18n.language)}</span>
                {sourceKey && <span className="cv2-eco-effective__src">{t(sourceKey)}</span>}
            </span>
        );
    };

    const ctaKey = stateKey === STATE_NOT_CONFIGURED ? 'clients_v2.economics.row.load'
        : (stateKey === STATE_REVIEW ? 'clients_v2.economics.row.review' : 'client_panel.economy.edit');

    return (
        <>
            <div className="cv2-cell cv2-eco-col-state">{renderState()}</div>
            <div className="cv2-cell cv2-eco-col-margin cv2-eco-num">{value(formatPct(economics.gross_margin_pct))}</div>
            <div className="cv2-cell cv2-eco-col-shipping cv2-eco-num">
                {value(formatFullMoney(currency, economics.shipping_per_order))}
            </div>
            <div className="cv2-cell cv2-eco-col-fee cv2-eco-num">{value(formatPct(economics.payment_fee_pct))}</div>
            <div className="cv2-cell cv2-eco-col-fixed cv2-eco-num">
                {value(formatFullMoney(currency, economics.fixed_costs_monthly))}
            </div>
            <div className="cv2-cell cv2-eco-col-agency_fee cv2-eco-num">
                {value(formatFullMoney(currency, economics.agency_fee_monthly))}
            </div>
            <div className="cv2-cell cv2-eco-col-breakeven cv2-eco-num">
                {valueOrWhy(formatRoas(economics.breakeven_roas), 'breakeven_roas')}
            </div>
            <div className={`cv2-cell cv2-eco-col-profit cv2-eco-num${configured && Number.isFinite(profit)
                && economics.estimated_profit !== null ? (profit >= 0 ? ' -pos' : ' -neg') : ''}`}>
                {valueOrWhy(formatSignedMoney(currency, economics.estimated_profit), 'estimated_profit')}
            </div>
            <div className="cv2-cell cv2-eco-col-effective">{renderEffective()}</div>
            <div className="cv2-cell cv2-eco-col-cta">
                {stateKey && stateKey !== STATE_UNAVAILABLE && (
                    <Link className="cv2-eco-cta" to={economiaPath} onClick={stopRow}
                          data-testid={`cv2-eco-cta-${client.id}`}>
                        {t(ctaKey)}
                    </Link>
                )}
            </div>
        </>
    );
}

export default ClientEconomicsCells;
